import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  CreateNotificationInput,
  Notification,
  NotificationRunResult,
  UpdateNotificationInput,
} from '@catalyst-psa/shared';

import { api } from '../../api/client.js';

const notificationKeys = {
  all: ['notifications'] as const,
  list: () => ['notifications', 'list'] as const,
};

export function useNotifications(): UseQueryResult<Notification[]> {
  return useQuery({
    queryKey: notificationKeys.list(),
    queryFn: () => api.get<Notification[]>('/notifications'),
  });
}

export function useCreateNotification(): UseMutationResult<
  Notification,
  unknown,
  CreateNotificationInput
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateNotificationInput) => api.post<Notification>('/notifications', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

export interface UpdateNotificationArgs {
  id: string;
  input: UpdateNotificationInput;
}

export function useUpdateNotification(): UseMutationResult<
  Notification,
  unknown,
  UpdateNotificationArgs
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: UpdateNotificationArgs) =>
      api.patch<Notification>(`/notifications/${id}`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

export function useDeleteNotification(): UseMutationResult<void, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/notifications/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

/** Runs a single rule immediately (evaluates pending records + sends). */
export function useRunNotification(): UseMutationResult<NotificationRunResult, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<NotificationRunResult>(`/notifications/${id}/run`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}
